/**
 * recentSearches — the last settled searches of this browser (`localStorage`, never the server: plan
 * `2026_11_01_wall_search_panel.md`, mechanism 5). A search is kept as its `q`, so a row of pills in the panel is
 * the entry read back through `parseQuery`. Every access to the storage is wrapped: a private window, blocked site
 * data or a full quota leave the panel working without Recent.
 */
import {parseQuery} from '@core/wall/tsQuery'

export const MAX_RECENT = 5
const KEY_PREFIX = 'wall.recent.'

/** The storage surface used here, so a test can hand in a map. */
export type RecentStore = Pick<Storage, 'getItem' | 'setItem'>

/** A search worth keeping: it holds a pill, or typed words with a word of some length. */
export function worthKeeping(q: string): boolean {
    const {pills, facets, words} = parseQuery(q)
    return pills.length > 0 || facets.length > 0 || words.trim().replace(/\s+/g, '').length >= 3
}

/** `q` first, a repeat of it (same pills, tokens and words) gone from the rest, cut to MAX_RECENT. */
export function pushRecent(list: readonly string[], q: string): string[] {
    const clean = q.trim()
    if (!worthKeeping(clean)) return [...list]
    return [clean, ...list.filter((entry) => entry !== clean)].slice(0, MAX_RECENT)
}

export function removeRecent(list: readonly string[], at: number): string[] {
    return list.filter((_, i) => i !== at)
}

export function readRecent(store: RecentStore | null, house: string): string[] {
    if (!store) return []
    try {
        const raw = JSON.parse(store.getItem(KEY_PREFIX + house) ?? '[]')
        if (!Array.isArray(raw)) return []
        return raw.filter((v): v is string => typeof v === 'string' && worthKeeping(v)).slice(0, MAX_RECENT)
    } catch {
        return []
    }
}

export function writeRecent(store: RecentStore | null, house: string, list: readonly string[]): void {
    if (!store) return
    try {
        store.setItem(KEY_PREFIX + house, JSON.stringify(list.slice(0, MAX_RECENT)))
    } catch {
        // storage is a convenience
    }
}

/** The browser's `localStorage`, or `null` where the accessor throws or the code runs on the server. */
export function browserStore(): RecentStore | null {
    try {
        return typeof window === 'undefined' ? null : window.localStorage
    } catch {
        return null
    }
}
